/**
 * Bidirectional conversion between recursive AstExpression trees and
 * React Flow flat node/edge arrays.
 *
 * This module is used internally by AstExpressionGraph — the flat format
 * never leaves that component.
 *
 * Additionally provides definition-level converters that wrap the expression
 * graph with Input, Hub, and Output nodes for the full decision graph view.
 */

import type {
  AstExpression,
  EditorNodeState,
  DecisionDefinition,
  EmitRule,
} from "@/types/decision-definition-schema";
import { generateNodeId } from "@/types/decision-definition-schema";
import type {
  ExpressionNode,
  ExpressionEdge,
  ExpressionNodeData,
  ExpressionNodeType,
  InputNodeData,
  HubNodeData,
  EmitOutputNodeData,
} from "@/types/decision-graph";
import { getLayoutedElements } from "@/components/react-flow";
import {
  getHubOutputHandles,
  resolveEmitSourceHandle,
  sortEmitRulesForLayout,
} from "@/components/decision-graph/utils/hub-handles";

// =============================================================================
// Tree → Flow (recursive tree to flat ReactFlow nodes + edges)
// =============================================================================

export interface TreeToFlowResult {
  nodes: ExpressionNode[];
  edges: ExpressionEdge[];
  /** ID of the expression tree root (feeds into the decision hub). */
  rootNodeId: string | null;
}

export function convertTreeToFlow(
  expression: AstExpression,
  editorState?: Record<string, EditorNodeState>
): TreeToFlowResult {
  const nodes: ExpressionNode[] = [];
  const edges: ExpressionEdge[] = [];

  const rootNodeId = walkTree(expression, nodes, edges, editorState);

  if (!editorState || Object.keys(editorState).length === 0) {
    const { nodes: laid } = getLayoutedElements(nodes, edges, {
      direction: "LR",
      nodeSeparation: 40,
      rankSeparation: 160,
    });
    return { nodes: laid as ExpressionNode[], edges, rootNodeId };
  }

  return { nodes, edges, rootNodeId };
}

function ensureNodeId(expr: AstExpression): string {
  if (!expr._nodeId) {
    expr._nodeId = generateNodeId();
  }
  return expr._nodeId;
}

function mapNodeType(astType: string): ExpressionNodeType {
  const map: Record<string, ExpressionNodeType> = {
    literal: "literal",
    fact_ref: "field",
    context_ref: "field",
    field: "field",
    comparison: "comparison",
    logical: "binary",
    binary: "binary",
    unary: "unary",
    exists: "existence",
    existence: "existence",
    is_true: "existence",
    is_false: "existence",
    is_unknown: "existence",
    case: "function",
    scorecard: "function",
    membership: "membership",
    function: "function",
  };
  return map[astType] || "function";
}

function buildNodeData(expr: AstExpression): ExpressionNodeData {
  const base = { label: expr.label || expr.type };

  switch (expr.type) {
    case "literal":
      return {
        ...base,
        nodeType: "literal" as const,
        value:
          expr.value === undefined || expr.value === null
            ? null
            : (expr.value as string | number | boolean | null),
        valueType: (expr.valueType as "string" | "number" | "boolean" | "null") || "string",
      };

    case "fact_ref":
    case "context_ref":
    case "field":
      return {
        ...base,
        nodeType: "field" as const,
        label: expr.label || expr.path || expr.type,
        path: expr.path || "",
        fieldType: expr.valueType as
          | "string"
          | "number"
          | "boolean"
          | "date"
          | undefined,
      };

    case "comparison":
      return {
        ...base,
        nodeType: "comparison" as const,
        label: expr.operator || "==",
        operator: (expr.operator as "==" | "!=" | "<" | "<=" | ">" | ">=") || "==",
      };

    case "logical":
    case "binary":
      return {
        ...base,
        nodeType: "binary" as const,
        label: (expr.operator || "AND").toUpperCase(),
        operator: ((expr.operator || "and").toUpperCase()) as "AND" | "OR",
      };

    case "unary":
      return {
        ...base,
        nodeType: "unary" as const,
        operator: "NOT" as const,
      };

    case "exists":
    case "existence":
      return {
        ...base,
        nodeType: "existence" as const,
        operator: "EXISTS" as const,
      };

    case "is_true":
      return {
        ...base,
        nodeType: "existence" as const,
        label: "IS TRUE",
        operator: "IS_SET" as const,
      };

    case "is_false":
      return {
        ...base,
        nodeType: "existence" as const,
        label: "IS FALSE",
        operator: "IS_SET" as const,
      };

    case "is_unknown":
      return {
        ...base,
        nodeType: "existence" as const,
        label: "IS UNKNOWN",
        operator: "IS_SET" as const,
      };

    case "membership":
      return {
        ...base,
        nodeType: "membership" as const,
        operator: ((expr.operator || "IN").toUpperCase()) as "IN" | "NOT_IN",
        setSize: expr.set?.length || 0,
      };

    case "case":
      return {
        ...base,
        nodeType: "function" as const,
        label: expr.label || "CASE",
        functionName: "case",
        argumentCount: expr.cases?.length || 0,
        _caseCount: expr.cases?.length || 0,
        _hasElse: !!expr.else,
      };

    case "scorecard":
      return {
        ...base,
        nodeType: "function" as const,
        label: expr.label || "SCORECARD",
        functionName: "scorecard",
        argumentCount: expr.factors?.length || 0,
        _factorMeta: expr.factors?.map((f) => ({
          id: f.id,
          label: f.label,
          points: f.points,
          reason: f.reason,
        })),
        _bands: expr.bands,
      };

    default:
      return {
        ...base,
        nodeType: "function" as const,
        functionName: expr.name || expr.type,
        argumentCount: expr.arguments?.length || 0,
      };
  }
}

function walkTree(
  expr: AstExpression,
  nodes: ExpressionNode[],
  edges: ExpressionEdge[],
  editorState?: Record<string, EditorNodeState>
): string {
  const nodeId = ensureNodeId(expr);
  const pos = editorState?.[nodeId];

  const node: ExpressionNode = {
    id: nodeId,
    type: mapNodeType(expr.type),
    position: pos ? { x: pos.x, y: pos.y } : { x: 0, y: 0 },
    data: buildNodeData(expr),
  };
  nodes.push(node);

  const addEdge = (
    childId: string,
    sourceHandle: string,
    targetHandle: string
  ) => {
    edges.push({
      id: `e-${childId}-${nodeId}-${targetHandle}`,
      source: childId,
      target: nodeId,
      sourceHandle,
      targetHandle,
      type: "expression",
      data: { edgeType: "value" as const },
    });
  };

  const addChild = (
    child: AstExpression,
    sourceHandle: string,
    targetHandle: string
  ) => {
    const childId = walkTree(child, nodes, edges, editorState);
    addEdge(childId, sourceHandle, targetHandle);
  };

  // Comparison / binary with explicit left/right
  if (expr.left) addChild(expr.left, "result", "left");
  if (expr.right) addChild(expr.right, "result", "right");

  // Logical / unary with children[] — map onto real node handles
  if (expr.children && expr.children.length > 0) {
    const op = (expr.operator || "").toLowerCase();
    if (op === "not" || expr.type === "unary") {
      addChild(expr.children[0], "result", "operand");
    } else {
      connectLogicalChildren(
        expr.children,
        nodeId,
        ((expr.operator || "and").toUpperCase() === "OR" ? "OR" : "AND") as
          | "AND"
          | "OR",
        nodes,
        edges,
        editorState
      );
    }
  }

  if (expr.target) addChild(expr.target, "result", "operand");
  if (expr.operand) addChild(expr.operand, "result", "operand");
  if (expr.set) {
    expr.set.forEach((s, i) => addChild(s, "result", `set-${i}`));
  }
  if (expr.arguments) {
    expr.arguments.forEach((a, i) => addChild(a, "result", `arg-${i}`));
  }

  // Case branches: each has a when condition and then result
  if (expr.cases) {
    expr.cases.forEach((c, i) => {
      addChild(c.when, "result", `case-when-${i}`);
      addChild(c.then, "result", `case-then-${i}`);
    });
  }
  if (expr.else) addChild(expr.else, "result", "else");

  // Scorecard factors: each has a when condition
  if (expr.factors) {
    expr.factors.forEach((f, i) => {
      addChild(f.when, "result", `factor-when-${i}`);
    });
  }

  return nodeId;
}

/**
 * Wire logical AND/OR children onto BinaryNode left/right handles.
 * For 3+ children, fold into a left-associated chain of binary nodes so
 * every sub-expression eventually funnels into the parent.
 */
function connectLogicalChildren(
  children: AstExpression[],
  parentNodeId: string,
  operator: "AND" | "OR",
  nodes: ExpressionNode[],
  edges: ExpressionEdge[],
  editorState?: Record<string, EditorNodeState>
): void {
  const link = (
    childId: string,
    targetId: string,
    targetHandle: "left" | "right"
  ) => {
    edges.push({
      id: `e-${childId}-${targetId}-${targetHandle}`,
      source: childId,
      target: targetId,
      sourceHandle: "result",
      targetHandle,
      type: "expression",
      data: { edgeType: "value" as const },
    });
  };

  if (children.length === 1) {
    const childId = walkTree(children[0], nodes, edges, editorState);
    link(childId, parentNodeId, "left");
    return;
  }

  if (children.length === 2) {
    const leftId = walkTree(children[0], nodes, edges, editorState);
    const rightId = walkTree(children[1], nodes, edges, editorState);
    link(leftId, parentNodeId, "left");
    link(rightId, parentNodeId, "right");
    return;
  }

  // 3+: nest first n-1 under an intermediate binary node on the left
  const intermediateId = generateNodeId();
  nodes.push({
    id: intermediateId,
    type: "binary",
    position: { x: 0, y: 0 },
    data: {
      nodeType: "binary",
      label: operator,
      operator,
    },
  });
  connectLogicalChildren(
    children.slice(0, -1),
    intermediateId,
    operator,
    nodes,
    edges,
    editorState
  );
  const lastId = walkTree(children[children.length - 1], nodes, edges, editorState);
  link(intermediateId, parentNodeId, "left");
  link(lastId, parentNodeId, "right");
}

// =============================================================================
// Flow → Tree (flat ReactFlow nodes + edges to recursive tree)
// =============================================================================

export interface FlowToTreeResult {
  expression: AstExpression;
  editorState: Record<string, EditorNodeState>;
}

export function exportFlowToTree(
  nodes: ExpressionNode[],
  edges: ExpressionEdge[]
): FlowToTreeResult | null {
  if (nodes.length === 0) return null;

  const editorState: Record<string, EditorNodeState> = {};
  for (const node of nodes) {
    editorState[node.id] = {
      x: node.position.x,
      y: node.position.y,
    };
  }

  const childTargets = new Set(edges.map((e) => e.source));
  const rootCandidates = nodes.filter((n) => {
    const isTarget = edges.some((e) => e.target === n.id);
    const isNotSource = !childTargets.has(n.id) || isTarget;
    return !edges.some((e) => e.target !== n.id && e.source === n.id) || isNotSource;
  });

  let rootNode = rootCandidates.find(
    (n) => !edges.some((e) => e.source === n.id)
  );
  if (!rootNode) {
    const targetIds = new Set(edges.map((e) => e.target));
    const sourceIds = new Set(edges.map((e) => e.source));
    rootNode = nodes.find((n) => targetIds.has(n.id) && !sourceIds.has(n.id));
  }
  if (!rootNode) rootNode = nodes[nodes.length - 1];

  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  const expression = buildTreeFromNode(rootNode.id, nodeMap, edges);

  return { expression, editorState };
}

function buildTreeFromNode(
  nodeId: string,
  nodeMap: Map<string, ExpressionNode>,
  edges: ExpressionEdge[]
): AstExpression {
  const node = nodeMap.get(nodeId);
  if (!node) {
    return { type: "literal", _nodeId: nodeId, value: null };
  }

  const incomingEdges = edges.filter((e) => e.target === nodeId);
  const getChild = (handle: string): AstExpression | undefined => {
    const edge = incomingEdges.find((e) => e.targetHandle === handle);
    if (!edge) return undefined;
    return buildTreeFromNode(edge.source, nodeMap, edges);
  };

  const getChildren = (prefix: string): AstExpression[] => {
    return incomingEdges
      .filter((e) => e.targetHandle?.startsWith(prefix))
      .sort((a, b) => (a.targetHandle || "").localeCompare(b.targetHandle || ""))
      .map((e) => buildTreeFromNode(e.source, nodeMap, edges));
  };

  const data = node.data;
  const expr: AstExpression = { type: "literal", _nodeId: nodeId };

  switch (data.nodeType) {
    case "literal":
      expr.type = "literal";
      expr.value = data.value;
      expr.valueType = data.valueType as AstExpression["valueType"];
      break;

    case "field":
      expr.type = "fact_ref";
      expr.path = data.path;
      expr.valueType = data.fieldType as AstExpression["valueType"];
      break;

    case "comparison":
      expr.type = "comparison";
      expr.operator = data.operator;
      expr.left = getChild("left");
      expr.right = getChild("right");
      break;

    case "binary":
      expr.type = "logical";
      expr.operator = data.operator.toLowerCase();
      expr.children =
        getChildren("child-").length > 0
          ? getChildren("child-")
          : [getChild("left"), getChild("right")].filter(
              Boolean
            ) as AstExpression[];
      break;

    case "unary":
      expr.type = "logical";
      expr.operator = "not";
      expr.children = [getChild("operand")].filter(
        Boolean
      ) as AstExpression[];
      break;

    case "existence":
      expr.type = "exists";
      expr.target = getChild("operand");
      break;

    case "membership":
      expr.type = "membership";
      expr.operator = data.operator.toLowerCase();
      expr.set = getChildren("set-");
      break;

    case "function":
      if (data.functionName === "case") {
        expr.type = "case";
        const whens = getChildren("case-when-");
        const thens = getChildren("case-then-");
        expr.cases = whens.map((w, i) => ({
          when: w,
          then: thens[i] || { type: "literal", value: null },
        }));
        const elseExpr = getChild("else");
        if (elseExpr) expr.else = elseExpr;
      } else if (data.functionName === "scorecard") {
        expr.type = "scorecard";
        const factorWhens = getChildren("factor-when-");
        const meta = ((data as Record<string, unknown>)._factorMeta || []) as Array<{
          id?: string;
          label: string;
          points: number;
          reason?: string;
        }>;
        expr.factors = factorWhens.map((w, i) => ({
          ...(meta[i] || { label: `Factor ${i + 1}`, points: 0 }),
          when: w,
        }));
        const bands = (data as Record<string, unknown>)._bands;
        if (Array.isArray(bands)) {
          expr.bands = bands as AstExpression["bands"];
        }
      } else {
        expr.type = "function";
        expr.name = data.functionName;
        expr.arguments = getChildren("arg-");
      }
      break;

    case "output":
      {
        const input = getChild("input");
        if (input) return input;
      }
      break;
  }

  if (data.label) expr.label = data.label;

  return expr;
}

// =============================================================================
// Definition → Full Graph (adds Input, Hub, Output nodes around expression)
// =============================================================================

export interface DefinitionToGraphResult {
  nodes: ExpressionNode[];
  edges: ExpressionEdge[];
}

function extractFactPaths(expr: AstExpression): string[] {
  const paths: string[] = [];
  const visited = new Set<string>();

  function walk(e: AstExpression) {
    if (e._nodeId && visited.has(e._nodeId)) return;
    if (e._nodeId) visited.add(e._nodeId);

    if ((e.type === "fact_ref" || e.type === "field") && e.path) {
      if (!paths.includes(e.path)) paths.push(e.path);
    }
    if (e.left) walk(e.left);
    if (e.right) walk(e.right);
    if (e.children) e.children.forEach(walk);
    if (e.target) walk(e.target);
    if (e.operand) walk(e.operand);
    if (e.set) e.set.forEach(walk);
    if (e.arguments) e.arguments.forEach(walk);
    if (e.cases) e.cases.forEach((c) => { walk(c.when); walk(c.then); });
    if (e.else) walk(e.else);
    if (e.factors) e.factors.forEach((f) => walk(f.when));
  }

  walk(expr);
  return paths;
}

export function convertDefinitionToGraph(
  definition: DecisionDefinition,
  editorState?: Record<string, EditorNodeState>
): DefinitionToGraphResult {
  const expressionResult = convertTreeToFlow(definition.root, editorState);
  const nodes: ExpressionNode[] = [...expressionResult.nodes];
  const edges: ExpressionEdge[] = [...expressionResult.edges];

  const factPaths = extractFactPaths(definition.root);

  // Input node
  const inputNodeId = "__input__";
  const inputData: InputNodeData = {
    nodeType: "input",
    label: "Input",
    inputSchemaRef: definition.schemas?.inputSchemaRef,
    factPaths,
  };
  const inputNode: ExpressionNode = {
    id: inputNodeId,
    type: "input",
    position: editorState?.[inputNodeId]
      ? { x: editorState[inputNodeId].x, y: editorState[inputNodeId].y }
      : { x: 0, y: 0 },
    data: inputData as unknown as ExpressionNodeData,
  };
  nodes.push(inputNode);

  // Connect input to fact_ref/field nodes
  const fieldNodes = expressionResult.nodes.filter(
    (n) => n.data.nodeType === "field"
  );
  for (const fieldNode of fieldNodes) {
    const hasIncoming = expressionResult.edges.some(
      (e) => e.target === fieldNode.id
    );
    if (!hasIncoming) {
      edges.push({
        id: `e-input-${fieldNode.id}`,
        source: inputNodeId,
        target: fieldNode.id,
        sourceHandle: "result",
        type: "expression",
        data: { edgeType: "value" as const },
      });
    }
  }

  // Hub node
  const hubNodeId = "__hub__";
  const decisionMode = definition.metadata?.mode || "tri_state";
  const hubData: HubNodeData = {
    nodeType: "hub",
    label: "Decision",
    decisionMode,
    emitRules: sortEmitRulesForLayout(definition.emitRules || []),
    scoreBands: definition.scoreBands || [],
  };
  const hubNode: ExpressionNode = {
    id: hubNodeId,
    type: "hub",
    position: editorState?.[hubNodeId]
      ? { x: editorState[hubNodeId].x, y: editorState[hubNodeId].y }
      : { x: 0, y: 0 },
    data: hubData as unknown as ExpressionNodeData,
  };
  nodes.push(hubNode);

  // Funnel the expression-tree root into the decision hub
  const rootNodeId = expressionResult.rootNodeId;
  if (rootNodeId) {
    edges.push({
      id: `e-${rootNodeId}-hub`,
      source: rootNodeId,
      target: hubNodeId,
      sourceHandle: "result",
      targetHandle: "input",
      type: "expression",
      data: { edgeType: "boolean" as const },
    });
  }

  // Output nodes (one per emit rule) — wire from mode-appropriate hub handles
  // Sort so layout order is approve → review → decline
  const orderedEmitRules = sortEmitRulesForLayout(definition.emitRules || []);
  const hubHandles = getHubOutputHandles(decisionMode, orderedEmitRules, definition.scoreBands || []);
  for (let i = 0; i < orderedEmitRules.length; i++) {
    const emitRule = orderedEmitRules[i];
    const emitNodeId = `__emit_${emitRule.id}__`;
    const emitData: EmitOutputNodeData = {
      nodeType: "emitOutput",
      label: emitRule.label || emitRule.emit?.outcome || "Output",
      emitRuleId: emitRule.id,
      outcome: emitRule.emit?.outcome,
      stopProcessing: emitRule.stopProcessing || false,
    };
    const emitNode: ExpressionNode = {
      id: emitNodeId,
      type: "emitOutput",
      position: editorState?.[emitNodeId]
        ? { x: editorState[emitNodeId].x, y: editorState[emitNodeId].y }
        : { x: 0, y: 0 },
      data: emitData as unknown as ExpressionNodeData,
    };
    nodes.push(emitNode);

    edges.push({
      id: `e-hub-${emitNodeId}`,
      source: hubNodeId,
      target: emitNodeId,
      sourceHandle: resolveEmitSourceHandle(decisionMode, emitRule, i, hubHandles),
      targetHandle: "input",
      type: "expression",
      data: { edgeType: "boolean" as const },
    });
  }

  // Layout if no saved positions
  const hasPositions = editorState && Object.keys(editorState).length > 0;
  if (!hasPositions) {
    const { nodes: laid } = getLayoutedElements(nodes, edges, {
      direction: "LR",
      nodeSeparation: 50,
      rankSeparation: 180,
    });
    return { nodes: laid as ExpressionNode[], edges };
  }

  return { nodes, edges };
}

// =============================================================================
// Full Graph → Definition (strips Input/Hub/Output, rebuilds definition)
// =============================================================================

export function exportGraphToDefinition(
  nodes: ExpressionNode[],
  edges: ExpressionEdge[],
  currentDefinition: DecisionDefinition
): DecisionDefinition {
  // Filter out structural nodes
  const structuralTypes = new Set(["input", "hub", "emitOutput", "table"]);
  const expressionNodes = nodes.filter(
    (n) => !structuralTypes.has(n.data.nodeType as string)
  );
  const structuralNodeIds = new Set(
    nodes.filter((n) => structuralTypes.has(n.data.nodeType as string)).map((n) => n.id)
  );

  // Filter edges that don't touch structural nodes
  const expressionEdges = edges.filter(
    (e) => !structuralNodeIds.has(e.source) && !structuralNodeIds.has(e.target)
  );

  // Export expression tree
  const treeResult = exportFlowToTree(
    expressionNodes as ExpressionNode[],
    expressionEdges as ExpressionEdge[]
  );

  // Rebuild emit rules from output nodes
  const emitOutputNodes = nodes.filter(
    (n) => (n.data as { nodeType: string }).nodeType === "emitOutput"
  );

  const updatedEmitRules: EmitRule[] = emitOutputNodes.map((node) => {
    const d = node.data as unknown as EmitOutputNodeData;
    const existing = currentDefinition.emitRules?.find(
      (r) => r.id === d.emitRuleId
    );
    if (existing) {
      return {
        ...existing,
        label: d.label,
        stopProcessing: d.stopProcessing,
        emit: {
          ...existing.emit,
          outcome: d.outcome || existing.emit?.outcome,
        },
      };
    }
    return {
      id: d.emitRuleId || generateNodeId(),
      label: d.label,
      when: { type: "literal", value: true },
      emit: {
        outcome: d.outcome,
        flags: [],
        reasons: [],
        obligations: [],
        data: {},
      },
      stopProcessing: d.stopProcessing,
    };
  });

  // Update input schema from input node
  const inputNode = nodes.find(
    (n) => (n.data as { nodeType: string }).nodeType === "input"
  );
  const inputData = inputNode?.data as unknown as InputNodeData | undefined;

  // Sync decision mode from hub node
  const hubNode = nodes.find(
    (n) => (n.data as { nodeType: string }).nodeType === "hub"
  );
  const hubData = hubNode?.data as unknown as HubNodeData | undefined;

  return {
    ...currentDefinition,
    root: treeResult?.expression || currentDefinition.root,
    emitRules: updatedEmitRules.length > 0 ? updatedEmitRules : currentDefinition.emitRules,
    scoreBands: hubData?.scoreBands || currentDefinition.scoreBands,
    metadata: {
      ...currentDefinition.metadata,
      ...(hubData?.decisionMode && { mode: hubData.decisionMode }),
    },
    schemas: {
      ...currentDefinition.schemas,
      ...(inputData?.inputSchemaRef !== undefined && {
        inputSchemaRef: inputData.inputSchemaRef,
      }),
    },
  };
}
